import { useState, useEffect, useCallback } from 'react'
import { type GeneratedImage2 } from '#app/routes/resources+/generated-images.js'
import { decodeImageFormat } from '#app/utils/helpers'

type Preset = {
	value: string
	multiplier: number
	disabled: boolean
}

export function useUpscaleDimensions(imageData: GeneratedImage2 | undefined) {
	const [upscaleWidth, setUpscaleWidth] = useState<number>(0)
	const [upscaleHeight, setUpscaleHeight] = useState<number>(0)
	const [selectedPreset, setSelectedPreset] = useState<string>('2x')

	const originalWidth = imageData?.format
		? decodeImageFormat(imageData.format)?.resolution?.width || 0
		: 0
	const originalHeight = imageData?.format
		? decodeImageFormat(imageData.format)?.resolution?.height || 0
		: 0

	const presets: Preset[] = [
		{ value: '1.25x', multiplier: 1.25 },
		{ value: '1.5x', multiplier: 1.5 },
		{ value: '1.75x', multiplier: 1.75 },
		{ value: '2x', multiplier: 2 },
		{ value: '2.5x', multiplier: 2.5 },
		{ value: '3x', multiplier: 3 },
		{ value: '4x', multiplier: 4 },
	].map((preset) => ({
		...preset,
		disabled:
			Math.max(
				originalWidth * preset.multiplier,
				originalHeight * preset.multiplier,
			) > 4096,
	}))

	useEffect(() => {
		if (originalWidth && originalHeight) {
			setUpscaleWidth(originalWidth * 2)
			setUpscaleHeight(originalHeight * 2)
		}
	}, [originalWidth, originalHeight])

	const handlePresetChange = useCallback(
		(value: string) => {
			setSelectedPreset(value)
			const preset = presets.find((p) => p.value === value)
			if (preset && !preset.disabled) {
				setUpscaleWidth(Math.round(originalWidth * preset.multiplier))
				setUpscaleHeight(Math.round(originalHeight * preset.multiplier))
			}
		},
		[originalWidth, originalHeight, presets],
	)

	const handleWidthChange = useCallback(
		(newWidth: number) => {
			setUpscaleWidth(newWidth)
			const aspectRatio = originalWidth / originalHeight
			setUpscaleHeight(Math.round(newWidth / aspectRatio))
			setSelectedPreset(
				presets.find((p) => p.multiplier === newWidth / originalWidth)?.value ||
					'',
			)
		},
		[originalWidth, originalHeight, presets],
	)

	const handleHeightChange = useCallback(
		(newHeight: number) => {
			setUpscaleHeight(newHeight)
			const aspectRatio = originalWidth / originalHeight
			setUpscaleWidth(Math.round(newHeight * aspectRatio))
			setSelectedPreset(
				presets.find((p) => p.multiplier === newHeight / originalHeight)
					?.value || '',
			)
		},
		[originalWidth, originalHeight, presets],
	)

	return {
		upscaleWidth,
		upscaleHeight,
		selectedPreset,
		handlePresetChange,
		handleWidthChange,
		handleHeightChange,
		presets,
	}
}
